import { useEffect, useMemo } from "react";
import {
    kernelAuthorChange,
    recordKernelAuthor,
} from "../lib/kernelAuthorship.js";
import { useCurrentUser } from "../lib/userInfo.js";

export default function KernelAuthorBadge({
    resolvedDefinition = null,
    candidate = null,
    record = false,
    tenantId: tenantIdProp = null,
    viewerUserId: viewerUserIdProp = null,
    onlyWhenChanged = false,
    className = "",
}) {
    const user = useCurrentUser();
    const tenantId =
        tenantIdProp ?? user?.currentTenant?.tenantId ?? null;
    const viewerUserId =
        viewerUserIdProp ?? user?.userId ?? null;
    const value = candidate || resolvedDefinition;
    const observation = useMemo(
        () => value
            ? kernelAuthorChange(value, { tenantId, viewerUserId })
            : null,
        [tenantId, value, viewerUserId],
    );

    useEffect(() => {
        if (!record || !value) return;
        recordKernelAuthor(value, { tenantId, viewerUserId });
    }, [record, tenantId, value, viewerUserId]);

    if (!observation) return null;
    if (onlyWhenChanged && !observation.changed) return null;
    const title = observation.changed
        ? `Author changed from ${observation.previous?.label || "Unknown author"} to ${observation.current.label} since you last ran /${observation.command}.`
        : `Kernel author: ${observation.current.label}`;

    return (
        <span
            data-kernel-author
            data-kernel-author-changed={observation.changed ? "true" : undefined}
            title={title}
            className={[
                "whitespace-nowrap font-mono text-[9.5px] leading-[1.2]",
                observation.changed
                    ? "font-semibold text-[color:var(--red,#c0392b)]"
                    : "text-[color:var(--muted-soft)]",
                className,
            ].join(" ")}
        >
            by {observation.current.label}
            {observation.changed ? " · author changed" : ""}
        </span>
    );
}
